import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { SparePart } from '@ecs-features/result/spare-parts-result/spare-parts.interface';
import { mediaQueries, tracking } from '@ecs-helpers';
import { BasketService } from '@ecs-services/basket.service';
import { LoadingLayerService } from '@ecs-services/loading-layer.service';
import { ModalService } from '@ecs-services/modal/modal.service';
import { SparePartService } from '@ecs-services/spare-part/spare-part.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HeaderLoginService, OnDestroyBase, SelectableRow, TrackingService } from '@os-components';
import { finalize, of, take, takeUntil } from 'rxjs';
import { CrossSellingPartData } from 'src/app/components/dialogs/part-configuration-wizard/components/cross-selling-table/cross-selling-table.interface';
import { GearOilConfigurationComponent } from 'src/app/components/dialogs/part-configuration-wizard/components/gear-oil-configuration/gear-oil-configuration.component';
import {
    DialogComponent,
    PartConfigurationWizardComponent,
    StepType,
    WizardStep,
} from 'src/app/components/dialogs/part-configuration-wizard/part-configuration-wizard.component';

import { BreakpointObserver } from '@angular/cdk/layout';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SewButton, SewHeadline, SewInput, SewSearchField } from '@sew/ui-components-angular/standalone';
import { GearOilOverviewComponent } from './components/gear-oil-overview/gear-oil-overview.component';

@Component({
    selector: 'ecs-gear-oil',
    templateUrl: './gear-oil.component.html',
    styleUrls: ['./gear-oil.component.scss'],
    imports: [
        SewHeadline,
        SewSearchField,
        SewInput,
        SewButton,
        NgTemplateOutlet,
        ReactiveFormsModule,
        FormsModule,
        TranslatePipe,
    ],
})
export class GearOilComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public isLoggedIn = false;
    public serialNumber?: string;
    public errorTextKey?: string;
    public isMobile = false;

    public constructor(
        private modalService: ModalService,
        private translateService: TranslateService,
        private trackingService: TrackingService,
        private headerLoginService: HeaderLoginService,
        private sparePartService: SparePartService,
        private basketService: BasketService,
        private loadingLayerService: LoadingLayerService,
        private breakpointObserver: BreakpointObserver
    ) {
        super();
    }

    public ngOnInit(): void {
        this.headerLoginService.loggedIn.pipe(takeUntil(this.destroySubject)).subscribe((isLoggedIn) => {
            this.isLoggedIn = isLoggedIn;
        });

        this.breakpointObserver
            .observe(mediaQueries.mq3)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ matches }) => {
                this.isMobile = !matches;
            });
    }

    public searchGearOil() {
        this.trackingService.trackEvent(tracking.step.accessoriesAndServices.gearOil.search);
        const steps: WizardStep<DialogComponent>[] = [];

        if (!this.serialNumber) {
            return;
        }

        this.loadingLayerService.showLayer();
        this.sparePartService
            .getGearOilPartsForSerialNumber(this.serialNumber)
            .pipe(
                finalize(() => this.loadingLayerService.hideLayer()),
                take(1)
            )
            .subscribe({
                next: (gearOilParts) => {
                    if (!gearOilParts || gearOilParts.length < 1) {
                        return;
                    }

                    steps.push({
                        component: GearOilConfigurationComponent,
                        data: {
                            title: this.translateService.instant('X_SELLING_DIALOG.GEAR_OIL.TAB_TITLE'),
                            stepData: {
                                type: StepType.GEAR_OIL,
                                parts: gearOilParts.map((gearOilPart) => ({
                                    ...gearOilPart.originalPart,
                                    uniqueId: `${gearOilPart.driveGroup.title}_${gearOilPart.originalPart.id}`,
                                })),
                                isLoggedIn: this.isLoggedIn,
                                items: of(
                                    gearOilParts.map(
                                        ({
                                            originalPart: {
                                                id,
                                                itemNumber,
                                                installedQuantity,
                                                gearOilQuantity,
                                                unit,
                                                name,
                                            },
                                            driveGroup,
                                            replacementParts,
                                        }) =>
                                            ({
                                                context: {
                                                    id,
                                                    uniqueId: `${driveGroup.title}_${id}`,
                                                    driveGroup,
                                                    itemNumber,
                                                    amount: installedQuantity,
                                                    gearOilQuantity,
                                                    gearOilUnit: unit,
                                                    name,
                                                },
                                                parts: replacementParts.map(
                                                    (part) =>
                                                        ({
                                                            rowData: part,
                                                            isSelectableRow: true,
                                                            isDisabled: false,
                                                        }) as SelectableRow<SparePart>
                                                ),
                                            }) as CrossSellingPartData
                                    )
                                ),
                            },
                            stepTrackingPoint: tracking.step.wizard.step.gearOil,
                        },
                        finished: false,
                    });
                    this.modalService
                        .openDialog({
                            component: PartConfigurationWizardComponent,
                            configuration: {
                                type: '',
                                headline: `${this.translateService.instant('CONFIGURATION.WIZARD.TITLE')} (${
                                    this.serialNumber
                                })`,
                                label: '',
                                open: true,
                                size: 'l',
                                status: '',
                                closeButton: true,
                            },
                            data: {
                                isLoggedIn: this.isLoggedIn,
                                steps,
                            },
                        })
                        .afterClosed$.subscribe((output) => {
                            if (output.data && output.data.length > 0) {
                                // empty serial number not possible, but compiler does not
                                // understand due to scope, therefore ignore this line
                                /* istanbul ignore next */
                                this.basketService.addProductsToBasket({
                                    searchterm: this.serialNumber ?? '',
                                    items: output.data,
                                });
                            }
                        });
                },
                error: (error: HttpErrorResponse) => {
                    switch (error.status) {
                        case 400:
                            this.errorTextKey = 'GEAR_OIL.ERROR.NO_PRODUCT_FOUND';
                            break;
                        case 404:
                            this.errorTextKey = 'GEAR_OIL.ERROR.NO_OIL_AVAILABLE';
                            break;
                        default:
                            this.errorTextKey = 'GEAR_OIL.ERROR.GENERIC';
                            break;
                    }
                },
            });
    }

    public gearOilOverview() {
        this.modalService
            .openDialog({
                component: GearOilOverviewComponent,
                configuration: {
                    type: '',
                    headline: `${this.translateService.instant('GEAROIL.OVERVIEW.DIALOG.TITLE')}`,
                    label: '',
                    open: true,
                    size: 'l',
                    status: '',
                    closeButton: true,
                },
            })
            .afterClosed$.subscribe((output) => {
                if (output.data) {
                    this.basketService.addProductsToBasket({
                        searchterm: '',
                        items: output.data,
                    });
                }
            });
    }

    public resetSearchError() {
        this.errorTextKey = undefined;
    }
}
